import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Fab, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FixedAssetList from "./FixedAssetList";
import FixedAssetDetail from "./FixedAssetDetail";
import AddFixedAssetModal from "./AddFixedAssetModal";
import TransferAssetModal from "./TransferAssetModal";
import AssetFilterModal from "./components/AssetFilterModal";
import { Postrequestcall } from "../../request/Postrequest";
import {
  FETCH_CLIENT_JOBS,
  FETCH_CLIENTS_EMPLOYEES_V2,
  FETCH_CLIENT_ASSET_CATEGORIES,
  FETCH_CLIENT_ASSET_SUB_CATEGORIES,
  FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES
} from "../../constant/Apipath";
import { debounce } from "lodash";

const INITIAL_FILTERS = {
  location: "",
  job: "",
  employee: "",
  jobId: "",
  employeeId: "",
  category: "",
  categoryId: "",
  subCategory: "",
  subCategoryId: "",
  status: "",
  statusId: ""
};

const FixedAssets = () => {
  const { selectedClientData, selectedAppPermissions } = useSelector((state) => state?.main);
  const isAdmin = selectedClientData?.IsAdmin === 1;
  const isEmployee = Boolean(selectedClientData?.ClientEmployeeId);
  const employeePermissions = selectedAppPermissions || [];
  
  const hasElevatedPermission = employeePermissions.some((perm) => {
    if (Number(perm?.AppId) !== 21) return false;
    const normalized = (perm?.PermissionName || "").toLowerCase().replace(/\s+/g, "");
    return ["add", "add_edit", "addedit", "all", "fullcontrol"].includes(normalized);
  });
  const canViewAllAssets = isAdmin || (isEmployee && hasElevatedPermission);

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [fabMenuAnchorEl, setFabMenuAnchorEl] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...INITIAL_FILTERS }));
  const [draftFilters, setDraftFilters] = useState(() => ({ ...INITIAL_FILTERS }));
  const [selectedFilterOption, setSelectedFilterOption] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  const { loginData } = useSelector((state) => state?.main);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Category, Subcategory, Status options
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchJobs = async (searchText = "") => {
    try {
      setJobLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 50,
        FullSearch: searchText,
        SearchList: [{ ClientId: selectedClientData?.ClientId, active: true }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
      };
      const res = await Postrequestcall(FETCH_CLIENT_JOBS, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const data = (res.data?.data || []).map((j) => ({
          id: j.id,
          label: `${j.jobCode} - ${j.name}`,
        }));
        setJobOptions(data);
      }
    } catch (e) {
      console.error("fetchJobs error:", e);
    } finally {
      setJobLoading(false);
    }
  };

  const fetchEmployees = async (searchText = "") => {
    try {
      setEmployeeLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 50,
        FullSearch: searchText,
        SearchList: [{ ClientId: selectedClientData?.ClientId, active: true }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
      };
      const res = await Postrequestcall(FETCH_CLIENTS_EMPLOYEES_V2, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const data = (res.data?.data || []).map((e) => ({
          id: e.id,
          label: `${e.firstName || ""} ${e.lastName || ""}`.trim(),
        }));
        setEmployeeOptions(data);
      }
    } catch (e) {
      console.error("fetchEmployees error:", e);
    } finally {
      setEmployeeLoading(false);
    }
  };

  const fetchCategories = async (searchText = "") => {
    try {
      setCategoryLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 100,
        FullSearch: searchText,
        SearchList: [{ ClientId: selectedClientData?.ClientId }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true
      };
      const res = await Postrequestcall(FETCH_CLIENT_ASSET_CATEGORIES, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const items = res.data?.data || [];
        setCategoryOptions(items.map((c) => ({ id: c.id, label: c.name })));
      }
    } catch (e) {
      console.error("fetchCategories error:", e);
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchSubCategories = async (categoryId, searchText = "") => {
    try {
      setSubCategoryLoading(true);
      const catId = categoryId ? Number(categoryId) : undefined;
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 100,
        PageNumber: 1,
        PageSize: 100,
        FullSearch: searchText || "",
        SearchString: searchText || "",
        ...(catId ? { CategoryId: catId } : {}),
        SearchList: [
          {
            ClientId: selectedClientData?.ClientId,
            ...(catId ? { AssetCategoryId: catId, CategoryId: catId } : {})
          }
        ],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true
      };
      const res = await Postrequestcall(FETCH_CLIENT_ASSET_SUB_CATEGORIES, payload, loginData?.accessToken);
      if (res?.status === 200 || res?.status === 201 || res?.data?.status) {
        const raw = res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
        const formatted = raw.map((sc) => ({
          id: sc.id ?? sc.Id,
          label: sc.name ?? sc.Name ?? sc.subCategoryName ?? sc.subCategory ?? ""
        }));
        setSubCategoryOptions(formatted);
      }
    } catch (e) {
      console.error("fetchSubCategories error:", e);
    } finally {
      setSubCategoryLoading(false);
    }
  };

  const fetchStatuses = async (searchText = "") => {
    try {
      setStatusLoading(true);
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 50,
        FullSearch: searchText,
        SearchList: [{ ClientId: selectedClientData?.ClientId }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true
      };
      const res = await Postrequestcall(FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const items = res.data?.data || [];
        setStatusOptions(items.map((s) => ({ id: s.id, label: s.name })));
      }
    } catch (e) {
      console.error("fetchStatuses error:", e);
    } finally {
      setStatusLoading(false);
    }
  };

  const debouncedFetchJobs = useCallback(
    debounce((searchText) => {
      fetchJobs(searchText);
    }, 500),
    []
  );

  const debouncedFetchEmployees = useCallback(
    debounce((searchText) => {
      fetchEmployees(searchText);
    }, 500),
    []
  );

  const debouncedFetchSubCategories = useCallback(
    debounce((catId, searchText) => {
      fetchSubCategories(catId, searchText);
    }, 500),
    []
  );

  const handleJobSearch = (searchText) => {
    setJobLoading(true);
    debouncedFetchJobs(searchText);
  };

  const handleEmployeeSearch = (searchText) => {
    setEmployeeLoading(true);
    debouncedFetchEmployees(searchText);
  };

  const handleSubCategorySearch = (catId, searchText) => {
    setSubCategoryLoading(true);
    debouncedFetchSubCategories(catId, searchText);
  };

  const handleRefreshList = () => setRefreshKey(prev => prev + 1);

  const handleClearFilter = (key) => {
    const reset = { ...appliedFilters, [key]: "" };
    if (key === "job") reset.jobId = "";
    if (key === "employee") reset.employeeId = "";
    if (key === "category") {
      reset.categoryId = "";
      reset.subCategory = "";
      reset.subCategoryId = "";
    }
    if (key === "subCategory") reset.subCategoryId = "";
    if (key === "status") reset.statusId = "";
    setAppliedFilters(reset);
    setDraftFilters(reset);
  };

  const handleClearAllFilters = () => {
    setAppliedFilters(INITIAL_FILTERS);
    setDraftFilters(INITIAL_FILTERS);
  };

  const determineSelectedOption = (filters) => {
    if (filters?.job || filters?.jobId) return "job";
    if (filters?.employee || filters?.employeeId) return "employee";
    if (filters?.location) return "location";
    if (filters?.category || filters?.categoryId) return "category";
    if (filters?.subCategory || filters?.subCategoryId) return "subCategory";
    if (filters?.status || filters?.statusId) return "status";
    return null;
  };

  const openFilterModal = () => {
    setDraftFilters({ ...appliedFilters });
    setSelectedFilterOption(determineSelectedOption(appliedFilters));
    setFilterModalOpen(true);
    fetchJobs("");
    fetchEmployees("");
    fetchCategories("");
    fetchStatuses("");
    fetchSubCategories(appliedFilters.categoryId || "", "");
  };

  const handleFilterApply = () => {
    setAppliedFilters({ ...draftFilters });
    setFilterModalOpen(false);
  };

  const handleFabOpen = (event) => {
    setFabMenuAnchorEl(event.currentTarget);
  };

  const handleFabClose = () => {
    setFabMenuAnchorEl(null);
  };

  const handleAddNewAsset = () => {
    handleFabClose();
    setAddModalOpen(true);
  };

  const handleTransferAsset = () => {
    handleFabClose();
    setTransferModalOpen(true);
  };

  const handleTransferClose = (shouldRefresh) => {
    setTransferModalOpen(false);
    if (shouldRefresh) {
      handleRefreshList(); // Or refresh detail if needed
      setDetailRefreshKey(prev => prev + 1);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f5f7fb", minHeight: "calc(100vh - 110px)", pb: 4, overflow: "scroll", scrollbarWidth: "none" }}>
      <Box sx={{ px: 2, pt: 2 }}>
        {selectedAsset ? (
          <FixedAssetDetail
            key={selectedAsset?.AssetId}
            selectedAsset={selectedAsset}
            onBack={() => setSelectedAsset(null)}
            onTransferClick={() => setTransferModalOpen(true)}
            refreshToken={detailRefreshKey}
          />
        ) : (
          <FixedAssetList
            key={refreshKey}
            onSelectAsset={(asset) => setSelectedAsset(asset)}
            appliedFilters={appliedFilters}
            onOpenFilter={openFilterModal}
            onClearFilter={handleClearFilter}
            onClearAllFilters={handleClearAllFilters}
            restrictToOwnAssets={!canViewAllAssets}
          />
        )}
      </Box>

      {canViewAllAssets && !selectedAsset && (
        <>
          <Fab
            color="primary"
            onClick={handleFabOpen}
            aria-label="asset actions"
            aria-controls={fabMenuAnchorEl ? "fixed-assets-fab-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={fabMenuAnchorEl ? "true" : undefined}
            sx={{ position: "fixed", bottom: 24, right: 24, color: 'white', background: selectedClientData?.AccentColor, "&:hover": { bgcolor: selectedClientData?.AccentColor } }}
          >
            <AddIcon />
          </Fab>

          <Menu
            id="fixed-assets-fab-menu"
            anchorEl={fabMenuAnchorEl}
            open={Boolean(fabMenuAnchorEl)}
            onClose={handleFabClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1.25,
                minWidth: 220,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "0px 14px 34px rgba(15, 23, 42, 0.16)",
              },
            }}
            MenuListProps={{
              dense: true,
              sx: { py: 0.75 },
            }}
          >
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.6 }}>
                Quick Actions
              </Typography>
            </Box>
            {
              hasElevatedPermission && (
                <MenuItem
                  onClick={handleAddNewAsset}
                  sx={{ py: 1.1, px: 2, gap: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: selectedClientData?.AccentColor }}>
                    <AddCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add New Asset"
                    primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                  />
                </MenuItem>
              )
            }

            {isAdmin && (
              <MenuItem
                onClick={handleTransferAsset}
                sx={{ py: 1.1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: selectedClientData?.AccentColor }}>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Transfer Asset"
                  primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                />
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {addModalOpen && (
        <AddFixedAssetModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onRefresh={handleRefreshList}
        />
      )}

      {filterModalOpen && (
        <AssetFilterModal
          filterModalOpen={filterModalOpen}
          setFilterModalOpen={setFilterModalOpen}
          draftFilters={draftFilters}
          setDraftFilters={setDraftFilters}
          handleClearAllFilters={handleClearAllFilters}
          handleFilterApply={handleFilterApply}
          selectedFilterOption={selectedFilterOption}
          setSelectedFilterOption={setSelectedFilterOption}
          accentColor={selectedClientData?.AccentColor}
          canViewAllAssets={canViewAllAssets}
          empID={selectedClientData?.ClientEmployeeId}
          jobOptions={jobOptions}
          jobLoading={jobLoading}
          onJobSearch={handleJobSearch}
          employeeOptions={employeeOptions}
          employeeLoading={employeeLoading}
          onEmployeeSearch={handleEmployeeSearch}
          categoryOptions={categoryOptions}
          categoryLoading={categoryLoading}
          onCategorySearch={fetchCategories}
          subCategoryOptions={subCategoryOptions}
          subCategoryLoading={subCategoryLoading}
          onSubCategorySearch={handleSubCategorySearch}
          onSubCategoryFetchImmediate={(catId, search) => fetchSubCategories(catId, search)}
          statusOptions={statusOptions}
          statusLoading={statusLoading}
          onStatusSearch={fetchStatuses}
        />
      )}

      {transferModalOpen && (
        <TransferAssetModal
          open={transferModalOpen}
          onClose={handleTransferClose}
          selectedAsset={selectedAsset}
        />
      )}
    </Box>
  );
};

export default FixedAssets;
